import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import { WellnessResource } from "types/wellnessResource";
import {
  useDeleteWellnessResourceMutation,
  useGetWellnessResourceQuery,
} from "graphql/__generated__/hooks";
import { MainLayoutContainer } from "components/Layout";
import { Add } from "@mui/icons-material";
import CreateOrEditWellnessResourceModal from "modals/CreateOrEditWellnessResourceModal/CreateOrEditWellnessResourceModal";
import { useAppSelector } from "hooks/useAppSelector";
import { userSelector } from "reduxStore/auth/selectors";
import { ROLES } from "types/user";
import { ResponsiveButton } from "components/Button";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useNotifications } from "hooks/useNotifications";
import { Link as LinkBase, Stack } from "@mui/material";

export function WellnessResourceList() {
  const { organizationId } = useParams();
  const navigation = useNavigate();
  const { notify } = useNotifications();
  const user = useAppSelector(userSelector);
  const isAdmin = React.useMemo(() => user?.role === ROLES.ADMIN, [user]);
  const [createWellnessResourceModal, setCreateWellnessResourceModal] =
    useState(false);
  const [deleteWellnessResourceModal, setDeleteWellnessResourceModal] =
    useState(false);
  const [wellnessResourceIdToDelete, setWellnessResourceIdToDelete] =
    useState("");
  const [wellnessResource, setWellnessResource] = useState(
    [] as WellnessResource[],
  );
  const [selectedWellnessResource, setSelectedWellnessResource] =
    useState<WellnessResource | null>(null);
  const [deleteWellnessResource] = useDeleteWellnessResourceMutation();
  const { loading, error, data, refetch } = useGetWellnessResourceQuery();
  const actions = useMemo(() => {
    if (isAdmin) {
      return [TABLE_ACTIONS.VIEW, TABLE_ACTIONS.EDIT, TABLE_ACTIONS.DELETE];
    }
    return [];
  }, [isAdmin]);

  useEffect(() => {
    setWellnessResource((data?.wellness_resource as WellnessResource[]) || []);
  }, [data]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.VIEW:
        navigation(`/organization/${organizationId}/wellness-resource/${id}`);
        break;
      case TABLE_ACTIONS.EDIT:
        const wellnessResourceToEdit = wellnessResource?.find(
          (wellnessResource) => wellnessResource.wellness_resource_id === id,
        );
        if (wellnessResourceToEdit) {
          setSelectedWellnessResource(wellnessResourceToEdit);
          setCreateWellnessResourceModal(true);
        }
        break;
      case TABLE_ACTIONS.DELETE:
        setWellnessResourceIdToDelete(id);
        setDeleteWellnessResourceModal(true);
        break;
      default:
        break;
    }
  };

  const handleCreateWellnessResourceModalOpen = () => {
    setCreateWellnessResourceModal(true);
  };

  const handleCreateOrEditWellnessResourceChange = () => {
    if (selectedWellnessResource !== null) {
      setSelectedWellnessResource(null);
    }
    setCreateWellnessResourceModal(false);
    refetch().then();
  };

  const handleDelete = async () => {
    if (!wellnessResourceIdToDelete || wellnessResourceIdToDelete === "") {
      return;
    }
    await deleteWellnessResource({
      variables: { id: wellnessResourceIdToDelete },
    });
    await refetch();
    setWellnessResourceIdToDelete("");
    notify({
      message: Messages.successResourceDeleted,
      severity: NotificationSeverity.success,
    });
  };

  return (
    <MainLayoutContainer
      title="Wellness Resource"
      loading={loading}
      error={error}
      buttonActions={
        isAdmin && (
          <ResponsiveButton
            onClick={() => handleCreateWellnessResourceModalOpen()}
            icon={<Add />}
          >
            New Wellness Resource
          </ResponsiveButton>
        )
      }
    >
      <CustomTable
        entities={wellnessResource}
        handleActionClick={handleActionClick}
        fieldToFilter={"name"}
        displayActionInMenu={isAdmin}
        columns={[
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "wellness_resource_id",
            value: (row: any) => (
              <Stack
                direction="row"
                spacing={2}
                alignItems={"center"}
                justifyContent={"flex-end"}
              >
                <LinkBase
                  sx={{ cursor: "pointer" }}
                  component={Link}
                  to={`/organization/${organizationId}/wellness-resource/${row.wellness_resource_id}`}
                >
                  View
                </LinkBase>
              </Stack>
            ),
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={actions}
        extraActionItems={[]}
        idColumn={"wellness_resource_id"}
      />

      {createWellnessResourceModal && (
        <CreateOrEditWellnessResourceModal
          handleCreateOrEditWellnessResourceChange={
            handleCreateOrEditWellnessResourceChange
          }
          createOrEditWellnessResourceModal={createWellnessResourceModal}
          handleCreateOrEditWellnessResourceModalDismiss={() => {
            setCreateWellnessResourceModal(false);
            if (selectedWellnessResource !== null) {
              setSelectedWellnessResource(null);
            }
          }}
          wellnessResource={selectedWellnessResource}
        />
      )}

      {deleteWellnessResourceModal && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={"Are you sure you want to delete this Wellness Resource?"}
          show={deleteWellnessResourceModal}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteWellnessResourceModal(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
}
